<script>
  import gql from 'graphql-tag'

  // Every mutation below returns this full fragment, so saving one section
  // never blanks the other.
  const WARRANTY_FIELDS = `
    supportPhone followUpAfterHours rewardCents maxRewardCents
    planRewards { planId title rewardCents retired }
  `

  const AMOUNT_FORMAT = /^\d+(\.\d{1,2})?$/

  const toDollars = cents => (cents == null ? '' : (cents / 100).toFixed(2))
  const toCents = dollars => (dollars === '' || dollars == null ? null : Math.round(Number(dollars) * 100))
  const formatMoney = cents => `$${toDollars(cents)}`

  export default {
    props: ['gid'],
    apollo: {
      settings: {
        fetchPolicy: "no-cache",
        query: gql`query($gid: ID) {
          merchant(gid: $gid) { merchantUser { settings {
            warranty { ${WARRANTY_FIELDS} }
          }}}
        }`,
        variables() {
          return { gid: this.gid }
        },
        update: data => data.merchant.merchantUser.settings
      }
    },
    data() {
      return {
        settings: {},
        showForm: false,
        showRewardsForm: false,
        saving: false,
        form: { supportPhone: null, followUpAfterHours: null },
        rewardsForm: { rewardDollars: '', plans: [] },
        rewardErrors: {}
      }
    },
    computed: {
      warranty() {
        return this.settings.warranty || {}
      },
      supportPhone() {
        return this.warranty.supportPhone
      },
      followUpAfterHours() {
        return this.warranty.followUpAfterHours
      },
      // Plans with their own amount, then the default for everything else.
      rewardRows() {
        const own = (this.warranty.planRewards || []).filter(plan => plan.rewardCents != null)
        if (!this.warranty.rewardCents && own.length === 0) return []

        const rows = own.map(plan => ({ key: plan.planId, label: plan.title, retired: plan.retired, cents: plan.rewardCents }))
        const offered = (this.warranty.planRewards || []).filter(plan => !plan.retired)
        const allOwn = offered.length > 0 && offered.every(plan => plan.rewardCents != null)
        if (!allOwn) rows.push({ key: 'default', label: own.length ? 'Other plans' : 'All plans', cents: this.warranty.rewardCents || 0 })
        return rows
      },
      maxDollars() {
        return toDollars(this.warranty.maxRewardCents || 0)
      },
      defaultPlaceholder() {
        return this.rewardsForm.rewardDollars === '' ? '0.00' : this.rewardsForm.rewardDollars
      },
      visiblePlans() {
        return this.rewardsForm.plans.filter(plan => !plan.removed)
      }
    },
    methods: {
      formatMoney,
      mergeWarranty(warranty) {
        this.settings = { ...this.settings, warranty: { ...this.settings.warranty, ...warranty } }
      },
      openEdit() {
        this.form.supportPhone = this.warranty.supportPhone
        this.form.followUpAfterHours = this.warranty.followUpAfterHours
        this.showForm = true
        this.$nextTick(() => M.updateTextFields())
      },
      openRewardsEdit() {
        this.rewardsForm.rewardDollars = toDollars(this.warranty.rewardCents || 0)
        this.rewardsForm.plans = (this.warranty.planRewards || []).map(plan => ({
          planId: plan.planId,
          title: plan.title,
          retired: plan.retired,
          removed: false,
          dollars: toDollars(plan.rewardCents)
        }))
        this.rewardErrors = {}
        this.showRewardsForm = true
      },
      amountError(value, { required }) {
        const text = String(value == null ? '' : value).trim()
        if (text === '') return required ? 'Enter an amount, or 0 for no reward' : null
        if (text.startsWith('-')) return "Can't be negative"
        if (!AMOUNT_FORMAT.test(text)) return 'Use dollars and cents, like 5.50'
        if (toCents(text) > this.warranty.maxRewardCents) return `Must be $${this.maxDollars} or less`
        return null
      },
      clearError(key) {
        if (this.rewardErrors[key]) this.$delete(this.rewardErrors, key)
      },
      validateRewards() {
        const errors = {}
        const defaultError = this.amountError(this.rewardsForm.rewardDollars, { required: true })
        if (defaultError) errors.default = defaultError
        this.visiblePlans.forEach(plan => {
          const error = this.amountError(plan.dollars, { required: false })
          if (error) errors[plan.planId] = error
        })
        this.rewardErrors = errors
        return Object.keys(errors).length === 0
      },
      save() {
        this.saving = true
        this.$apollo.mutate({
          mutation: gql`mutation (
            $gid: ID!,
            $supportPhone: String,
            $followUpAfterHours: Int
          ) {
            updateWarrantyPreferences(input: {
              userId: $gid,
              supportPhone: $supportPhone,
              followUpAfterHours: $followUpAfterHours
            }) {
              warranty { ${WARRANTY_FIELDS} }
              errors
            }
          }`,
          variables: {
            gid: this.gid,
            supportPhone: this.form.supportPhone === '' ? null : this.form.supportPhone,
            followUpAfterHours: this.form.followUpAfterHours === '' ? null : this.form.followUpAfterHours
          }
        }).then(result => {
          const payload = result.data.updateWarrantyPreferences
          if (payload.errors && payload.errors.length) {
            this.notifyError(payload.errors.join(', '))
            return
          }
          this.mergeWarranty(payload.warranty)
          this.showForm = false
          this.notifySuccess("Warranty follow-up saved")
        }, error => {
          this.gqlErrorHandler(error)
        }).finally(() => { this.saving = false })
      },
      // The form always shows the full stored state, so every plan is sent:
      // a blank amount (or a removed retired plan) is an explicit null, which
      // clears the plan's own reward.
      saveRewards() {
        if (!this.validateRewards()) return

        this.saving = true
        this.$apollo.mutate({
          mutation: gql`mutation (
            $gid: ID!,
            $rewardCents: Int,
            $planRewards: [WarrantyPlanRewardInput!]
          ) {
            updateWarrantyRewards(input: {
              userId: $gid,
              rewardCents: $rewardCents,
              planRewards: $planRewards
            }) {
              warranty { ${WARRANTY_FIELDS} }
              errors
            }
          }`,
          variables: {
            gid: this.gid,
            rewardCents: toCents(this.rewardsForm.rewardDollars),
            planRewards: this.rewardsForm.plans.map(plan => ({
              planId: plan.planId,
              rewardCents: plan.removed ? null : toCents(plan.dollars)
            }))
          }
        }).then(result => {
          const payload = result.data.updateWarrantyRewards
          if (payload.errors && payload.errors.length) {
            this.notifyError(payload.errors.join(', '))
            return
          }
          this.mergeWarranty(payload.warranty)
          this.showRewardsForm = false
          this.notifySuccess("Tech rewards saved")
        }, error => {
          this.gqlErrorHandler(error)
        }).finally(() => { this.saving = false })
      }
    }
  }
</script>

<template>
  <div v-if="!$apollo.queries.settings.loading" class="warranty-prefs">
    <section class="wp-section">
      <div class="wp-head">
        <div class="very-close-lines">
          Warranty follow-up SMS:<br>
          <small class="grey-text">One text to customers who skipped the warranty offer</small>
        </div>
        <a v-if="settings.warranty && !showForm" class="pointer wp-edit" @click="openEdit"><i class="far fa-edit"></i> Edit</a>
      </div>

      <div v-if="settings.warranty" class="mt-5 grey-text">
        After:
        <template v-if="followUpAfterHours != null">{{ followUpAfterHours }} hour(s)</template>
        <template v-else>off</template>
        · Support phone:
        <template v-if="supportPhone">{{ supportPhone }}</template>
        <template v-else>profile phone</template>
      </div>

      <div v-else class="mt-5 grey-text">
        Warranty not enabled
      </div>

      <div class="row mb-0 mt-10" v-show="showForm">
        <div class="input-field col s6">
          <input id="warranty_follow_up_after_hours" type="number" min="1" max="168" v-model.number="form.followUpAfterHours">
          <label for="warranty_follow_up_after_hours" class="active">Follow up after (hours)</label>
          <span class="helper-text grey-text">1–168; blank turns follow-ups off</span>
        </div>
        <div class="input-field col s6">
          <input id="warranty_support_phone" type="tel" v-model.trim="form.supportPhone">
          <label for="warranty_support_phone" class="active">Support phone</label>
          <span class="helper-text grey-text">Blank uses the profile phone</span>
        </div>
        <div class="col s12">
          <button class="btn-small emerald" @click="save" :disabled="saving">Save follow-up</button>
          <a class="pointer ml-10 grey-text" @click="showForm = false">Cancel</a>
        </div>
      </div>
    </section>

    <section v-if="settings.warranty" class="wp-section">
      <div class="wp-head">
        <div class="very-close-lines">
          Warranty tech reward:<br>
          <small class="grey-text">Paid to the selling account for each successful warranty charge</small>
        </div>
        <a v-if="!showRewardsForm" class="pointer wp-edit" @click="openRewardsEdit"><i class="far fa-edit"></i> Edit</a>
      </div>

      <template v-if="!showRewardsForm">
        <div v-if="rewardRows.length === 0" class="mt-5 grey-text">No reward set</div>
        <ul v-else class="wp-sheet">
          <li v-for="row in rewardRows" :key="row.key" class="wp-row">
            <span class="wp-name">
              {{ row.label }}
              <span v-if="row.retired" class="badge-grey-outlined-condenced">retired</span>
            </span>
            <span class="wp-leader" aria-hidden="true"></span>
            <span class="wp-amount" :class="{ 'wp-amount--none': row.cents === 0 }">
              {{ row.cents === 0 ? 'No reward' : formatMoney(row.cents) }}
            </span>
          </li>
        </ul>
      </template>

      <form v-else class="wp-sheet wp-sheet--edit" novalidate @submit.prevent="saveRewards">
        <div class="wp-row">
          <label for="warranty_reward_default" class="wp-name">
            Default
            <small class="wp-note">paid on any plan left blank</small>
          </label>
          <span class="wp-leader" aria-hidden="true"></span>
          <span class="wp-money" :class="{ 'wp-money--error': rewardErrors.default }">
            <span class="wp-currency">$</span>
            <input id="warranty_reward_default" class="browser-default wp-input" type="text" inputmode="decimal"
                   autocomplete="off" v-model.trim="rewardsForm.rewardDollars" :aria-invalid="!!rewardErrors.default"
                   @input="clearError('default')">
          </span>
        </div>
        <p v-if="rewardErrors.default" class="wp-error" role="alert">{{ rewardErrors.default }}</p>

        <template v-for="plan in visiblePlans">
          <div class="wp-row" :key="plan.planId">
            <label :for="`warranty_reward_${plan.planId}`" class="wp-name">
              {{ plan.title }}
              <span v-if="plan.retired" class="badge-grey-outlined-condenced">retired</span>
              <a v-if="plan.retired" class="pointer wp-remove" @click="plan.removed = true">remove</a>
            </label>
            <span class="wp-leader" aria-hidden="true"></span>
            <span class="wp-money" :class="{ 'wp-money--error': rewardErrors[plan.planId] }">
              <span class="wp-currency">$</span>
              <input :id="`warranty_reward_${plan.planId}`" class="browser-default wp-input" type="text" inputmode="decimal"
                     autocomplete="off" :placeholder="defaultPlaceholder" v-model.trim="plan.dollars"
                     :aria-invalid="!!rewardErrors[plan.planId]" @input="clearError(plan.planId)">
            </span>
          </div>
          <p v-if="rewardErrors[plan.planId]" :key="`${plan.planId}-error`" class="wp-error" role="alert">{{ rewardErrors[plan.planId] }}</p>
        </template>

        <p class="wp-hint grey-text">Leave a plan blank to pay the default. Up to ${{ maxDollars }} per charge; 0 pays nothing.</p>

        <div class="mt-10">
          <button type="submit" class="btn-small emerald" :disabled="saving">{{ saving ? 'Saving…' : 'Save rewards' }}</button>
          <a class="pointer ml-10 grey-text" @click="showRewardsForm = false">Cancel</a>
        </div>
      </form>
    </section>
  </div>
</template>

<style scoped>
  /* The page's mt-15 wrapper class isn't defined, so the widget spaces itself. */
  .warranty-prefs {
    margin-top: 20px;
  }

  .wp-section + .wp-section {
    margin-top: 20px;
  }

  .wp-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .wp-edit {
    white-space: nowrap;
  }

  /* Rate sheet: plan on the left, amount on the right, joined by a dotted leader. */
  .wp-sheet {
    margin: 8px 0 0;
    padding: 0;
    max-width: 420px;
  }

  .wp-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-height: 30px;
    list-style: none;
  }

  .wp-name {
    color: #1f2a37;
    font-size: 15px;
    min-width: 0;
  }

  .wp-note {
    display: block;
    color: #6b7280;
    font-size: 12px;
  }

  .wp-leader {
    flex: 1 1 24px;
    min-width: 24px;
    border-bottom: 1px dotted #c3c8cf;
    transform: translateY(-4px);
  }

  .wp-amount {
    color: #1f2a37;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .wp-amount--none {
    color: #6b7280;
  }

  .wp-sheet--edit .wp-row {
    min-height: 40px;
  }

  .wp-money {
    display: inline-flex;
    align-items: baseline;
    border-bottom: 1px solid #c3c8cf;
    padding: 2px 0;
    transition: border-color .15s;
  }

  .wp-money:focus-within {
    border-bottom-color: #00ab8e;
    box-shadow: 0 1px 0 0 #00ab8e;
  }

  .wp-money--error,
  .wp-money--error:focus-within {
    border-bottom-color: #c62828;
    box-shadow: 0 1px 0 0 #c62828;
  }

  .wp-currency {
    color: #6b7280;
    padding-right: 2px;
  }

  .wp-input {
    width: 72px;
    border: 0;
    outline: none;
    background: transparent;
    text-align: right;
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    color: #1f2a37;
  }

  .wp-input::placeholder {
    color: #b0b6be;
  }

  .wp-remove {
    font-size: 12px;
    margin-left: 4px;
  }

  .wp-error {
    margin: 0 0 4px;
    text-align: right;
    max-width: 420px;
    color: #c62828;
    font-size: 12px;
  }

  .wp-hint {
    margin: 8px 0 0;
    max-width: 420px;
    font-size: 12px;
  }

  @media (prefers-reduced-motion: reduce) {
    .wp-money {
      transition: none;
    }
  }
</style>
